/**
 * @file NotificationTemplateDetailsPanel.js
 * @description Metadata fields (name, category, scope, description, tier availability, status) for the
 * multitenant notification template editor. Agency options are pre-filtered by the caller to the
 * agencies the current user actually has access to (plus Global, when applicable).
 */
import { TextAreaField, CheckboxField } from '../../helpers/FormComponents';
import { Dropdown } from '../../helpers/DropdownSearchableSingleSelect';
import { TenantTiers, tenantTierLabels } from '../../../utils/data/enums';
import { getText } from '../../../i18n';
import styles from './NotificationTemplateDetailsPanel.module.scss';

const SCOPE_TIER_IDS = [TenantTiers.Base, TenantTiers.Bronze];

const NotificationTemplateDetailsPanel = ({
    templateData,
    categories,
    agencyOptions,
    isReadOnly,
    showTierSection = true,
    onNameChange,
    onDescriptionChange,
    onCategoryChange,
    onAgencyChange,
    onTierToggle,
}) => {
    const categoryOptions = categories.map(category => ({
        value: category.id,
        label: category.name,
    }));

    const selectedCategoryLabel =
        categoryOptions.find(
            option => option.value === templateData.notificationCategoryId,
        )?.label || 'Select';

    const selectedAgencyLabel =
        agencyOptions.find(option => option.value === templateData.agencyId)
            ?.label ?? (templateData.agencyId === 0 ? 'Global' : 'Unknown');

    return (
        <div className={styles['notification-template-details-shell']}>
            <div className={styles['notification-template-top-row']}>
                <div className={styles['notification-template-name-column']}>
                    <div
                        className={`file-form-label ${styles['notification-template-field-label']}`}
                    >
                        {getText('msg_709a23220f2c')}
                        <span
                            style={{
                                color: 'var(--iNGAGED-Destructive-Color)',
                                marginLeft: '4px',
                            }}
                        >
                            *
                        </span>
                    </div>

                    <div
                        className={
                            styles['notification-template-field-control']
                        }
                    >
                        <input
                            type="text"
                            className={`form-control ${styles['notification-template-text-input']}`}
                            value={templateData.name || ''}
                            onChange={event => onNameChange(event.target.value)}
                            disabled={isReadOnly}
                            maxLength={50}
                            placeholder={getText('msg_c571d3ec6ec7')}
                        />
                    </div>

                    <div
                        className={
                            styles['notification-template-character-limit']
                        }
                    >
                        {`Character Limit ${templateData.name?.length || 0}/50`}
                    </div>
                </div>

                <div className={styles['notification-template-select-column']}>
                    <div
                        className={`file-form-label ${styles['notification-template-field-label']}`}
                    >
                        {getText('msg_a3c686e711e4')}
                        <span
                            style={{
                                color: 'var(--iNGAGED-Destructive-Color)',
                                marginLeft: '4px',
                            }}
                        >
                            *
                        </span>
                    </div>
                    <div
                        className={
                            styles['notification-template-field-control']
                        }
                    >
                        <div
                            className={
                                styles['notification-template-dropdown-fill']
                            }
                        >
                            <Dropdown
                                options={categoryOptions}
                                defaultValue={selectedCategoryLabel}
                                onChange={onCategoryChange}
                                disabled={isReadOnly}
                            />
                        </div>
                    </div>
                </div>

                <div className={styles['notification-template-select-column']}>
                    <div
                        className={`file-form-label ${styles['notification-template-field-label']}`}
                    >
                        {getText('msg_073c4e1f6953')}
                        <span
                            style={{
                                color: 'var(--iNGAGED-Destructive-Color)',
                                marginLeft: '4px',
                            }}
                        >
                            *
                        </span>
                    </div>
                    <div
                        className={
                            styles['notification-template-field-control']
                        }
                    >
                        <div
                            className={
                                styles['notification-template-dropdown-fill']
                            }
                        >
                            <Dropdown
                                options={agencyOptions}
                                defaultValue={selectedAgencyLabel}
                                onChange={onAgencyChange}
                                disabled={isReadOnly}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div
                className={styles['notification-template-description-section']}
            >
                <TextAreaField
                    label={getText('msg_55f8ebc805e6')}
                    onChange={onDescriptionChange}
                    maxChars={500}
                    value={templateData.description}
                    disabled={isReadOnly}
                    placeholder={getText('msg_5f8e9d7c6b21')}
                />
            </div>

            {showTierSection && (
                <div className={styles['notification-template-tier-section']}>
                    {SCOPE_TIER_IDS.map(tierId => (
                        <CheckboxField
                            key={tierId}
                            label={`Available to ${tenantTierLabels[tierId]}`}
                            checked={(
                                templateData.availableTierIds || []
                            ).includes(tierId)}
                            onChange={checked => onTierToggle(tierId, checked)}
                            disabled={isReadOnly}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default NotificationTemplateDetailsPanel;
